// Shared API error envelope + axios error mapper, used by every adapter.
//
// Server responses follow `{ error: { code, message, fields?, meta? } }`
// (see docs/api-contracts/my-profile.md §1.5). Adapters catch axios errors
// and re-throw `ApiError` so consumers can switch on `err.code`.

import axios from 'axios';

interface ApiErrorEnvelope {
  error: {
    code: string;
    message: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

export class ApiError extends Error {
  code: string;
  fields?: Record<string, string>;
  meta?: Record<string, unknown>;

  constructor(
    code: string,
    message: string,
    fields?: Record<string, string>,
    meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.fields = fields;
    this.meta = meta;
  }
}

export function mapAxiosError(err: unknown): ApiError {
  if (axios.isAxiosError(err)) {
    const envelope = err.response?.data as ApiErrorEnvelope | undefined;
    if (envelope?.error) {
      return new ApiError(
        envelope.error.code ?? 'INTERNAL_ERROR',
        envelope.error.message ?? 'Unknown error',
        envelope.error.fields,
        envelope.error.meta,
      );
    }
    if (!err.response) {
      // No response at all (server unreachable / DNS / timeout / CORS preflight failure). CLAUDE.md §7.1
      // rule 3 wants this told apart from a server-sent failure so callers can pick the right System-Error
      // copy — PRD 00 §8.2 "서버 응답 없음" toast vs the generic system-error toast (SHCT-322).
      return new ApiError('NETWORK', err.message);
    }
    if (err.response?.status === 401) {
      // 401 with no error envelope (e.g. a gateway/filter rejection before the body is built). Carry only
      // the wire code — the FE maps a code → its Korean label (never hardcode Korean here); the response
      // interceptor handles the redirect to /login, so this message is not surfaced to the user.
      return new ApiError('UNAUTHENTICATED', '');
    }
  }
  return new ApiError(
    'INTERNAL_ERROR',
    err instanceof Error ? err.message : 'Unknown error',
  );
}
